// src/components/AdminDashboard.js - Screen Management (View & Control Only) - CRITICAL FIXES APPLIED
import React, { useState, useEffect } from 'react';
import EventList from './EventList';
import EventDetail from './EventDetail';
import BackgroundControl from './BackgroundControl';
import CouplesList from './CouplesList';
import CallbacksControl from './CallbacksControl';
import { useCompetition } from '../competition/CompetitionContext';
import './AdminDashboard.css';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';
const _company_url = 'wdsf-scrutiny.flokraft.dance';

const AdminDashboard = () => {
  const { selectedCompetition } = useCompetition();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('events'); // 'events', 'dancers', 'callbacks', or 'backgrounds'

  // Function to fetch events from the API
  const fetchEvents = async () => {
    try {
      // Check if we have a selected competition
      if (!selectedCompetition) {
        setError('No competition selected');
        setLoading(false);
        return;
      }

      let url;
      let data;

      // Try with competition filtering first
      try {
        url = selectedCompetition
          ? `https://${_host}:6003/api/${_company}/events?competitionId=${selectedCompetition.id}`
          : `https://${_host}:6003/api/${_company}/events`;
        const response = await fetch(url);

        if (response.ok) {
          data = await response.json();
          setEvents(data);
          setLoading(false);
          return;
        }
      } catch (compFilterError) {
        console.log('Competition filtering not supported, trying fallback...');
      }

      // Fallback to all events (for servers without competition support)
      console.log('Fetching all events (competition filtering not available)');
      url = `https://${_host}:6003/api/${_company}/events`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch events');
      }

      data = await response.json();

      // Filter events locally if server doesn't support it
      const filteredEvents = data.filter(event =>
        !selectedCompetition || event.competitionId === selectedCompetition.id
      );

      setEvents(filteredEvents);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events. Please try again later.');
      setLoading(false);
    }
  };

  // Fetch events when component mounts or when competition changes
  useEffect(() => {
    fetchEvents();

    // Set up polling to refresh events periodically
    const interval = setInterval(fetchEvents, 5000); // Poll every 5 seconds

    // Clean up interval on component unmount
    return () => clearInterval(interval);
  }, [selectedCompetition]); // Add selectedCompetition to dependency array

  // Function to select an event
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setActiveTab('events');
  };

  // Function to update display data
  const handleUpdateDisplay = async (displayData) => {
    try {
      const response = await fetch('https://'+_host+':6003/api/'+_company+'/display', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(displayData)
      });

      if (!response.ok) {
        throw new Error('Failed to update display');
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating display:", error);
      throw error;
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="page-header">
        <h1>Screen Management</h1>
        <p className="page-subtitle">Control competitions and public displays</p>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="dashboard-tabs">
        <button
          className={`dashboard-tab ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          <span className="tab-icon">📅</span>
          <span className="tab-label">Events</span>
        </button>
          <button
          className={`dashboard-tab ${activeTab === 'callbacks' ? 'active' : ''}`}
          onClick={() => setActiveTab('callbacks')}
        >
          <span className="tab-icon">📋</span>
          <span className="tab-label">Callbacks Display</span>
        </button>
        <button
          className={`dashboard-tab ${activeTab === 'backgrounds' ? 'active' : ''}`}
          onClick={() => setActiveTab('backgrounds')}
        >
          <span className="tab-icon">🖼️</span>
          <span className="tab-label">Display Backgrounds</span>
        </button>
        <button
          className="dashboard-display-btn"
          onClick={() => window.open(`${window.location.origin}/display`, '_blank')}
        >
          <span className="tab-icon">🔥</span>
          <span className="tab-label">Open Heat Display Screen</span>
        </button>
        <button
          className="dashboard-display-btn"
          onClick={() => window.open(`${window.location.origin}/callbacks`, '_blank')}
        >
          <span className="tab-icon">📋</span>
          <span className="tab-label">Open Callbacks Screen</span>
        </button>
      </div>

      {activeTab === 'events' ? (
        <div className="dashboard-container">
          <div className="dashboard-content">
            <div className="sidebar">
              <EventList
                events={events}
                loading={loading}
                onSelectEvent={handleSelectEvent}
                selectedEventId={selectedEvent?.id}
                onDeleteEvent={null}
                showDeleteOnly={false}
                filterBroadcastLive={true}
              />
            </div>

            <div className="main-content scrollable-content">
              {selectedEvent ? (
                <EventDetail
                  event={selectedEvent}
                  onUpdateDisplay={handleUpdateDisplay}
                  onEventUpdated={fetchEvents}
                />
              ) : (
                <div className="placeholder-message">
                  <h2>Welcome to Screen Management</h2>
                  <p>Select an event from the sidebar to control the competition.</p>
                  {events.length === 0 && !loading && (
                    <div className="getting-started-guide">
                      <h3>No Events Available</h3>
                      <p>Go to <strong>Administration</strong> to create events and add dancers.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : activeTab === 'callbacks' ? (
        <div className="dashboard-container">
          <div className="dashboard-content">
            <div className="full-width-content scrollable-content">
              <CallbacksControl />
            </div>
          </div>
        </div>
      ) : (
        <div className="dashboard-container">
          <div className="dashboard-content">
            <div className="full-width-content scrollable-content">
              <BackgroundControl />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
